import BellIcon from '@assets/icons/outline/bell.svg?react';
import ChevronRightIcon from '@assets/icons/outline/chevron-right.svg?react';
import FireIcon from '@assets/icons/outline/flame.svg?react';
import PricesIcon from '@assets/icons/outline/coins.svg?react';
import NewsIcon from '@assets/icons/outline/newspaper.svg?react';
import { useNavigate, Link } from 'react-router-dom';
import Skeleton from "@components/new/Skeleton";
import { HoverCard, IHoverCardContent } from '@components/new/HoverCard';
import { Title2, Title3 } from "@components/new/Typo";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import Icon from "@components/new/Icon";
import { useNotificationsUnreadCount } from '@api/apiClient/notifications';
import { useEffect, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getNotifications } from '@api/apiClient/notifications';
import cn from "classnames";
import { useProfile } from '@contexts/ProfileContext';
import styles from "./NotificationsWidget.module.scss";

const icons: Record<string, React.ReactNode> = {
  Important: <FireIcon className={cn(styles.itemIcon, styles.iconImpoirtant)} />,
  OffersAndPricing: <PricesIcon className={cn(styles.itemIcon, styles.iconOffers)} />,
  News: <NewsIcon className={cn(styles.itemIcon, styles.iconNews)} />,
}

const tabs: Record<string, string> = {
  Important: "important",
  OffersAndPricing: "offersAndPricing",
  News: "news",
  Unknown: "all"
}

const NotificationsWidget = () => {
  const [areThereUnreadNews, setAreThereUnreadNews] = useState<number>(0);
  const navigate = useNavigate();
  const { account } = useProfile();
  const currentLanguage = account!.region;
  const { t } = useTranslation();

  const {
    data: notificationsCount,
    refetch,
  } = useNotificationsUnreadCount(currentLanguage);

  const {
    data: notificationsData,
    isLoading,
  } = useQuery({
    queryKey: ['getNotifications', "", "unread", 1, 4],
    queryFn: () => getNotifications({
      page: { page: 1, pageSize: 4 },
      filters: [
        { name: 'Language', value: currentLanguage },
        // { name: 'Category', value: "all" },
        { name: 'IsRead', value: 'false' },
      ],
    }),
    retry: 1,
  });

  const Action = useMemo(() => () => (
    <div
      className={styles.action}
      onClick={() => navigate('/notifications?tab=news')}
    >
      <Icon icon={BellIcon} />
      {areThereUnreadNews ? (
        <div className={styles.tag}>{areThereUnreadNews > 99 ? "99+" : areThereUnreadNews}</div>
      ) : null}
    </div>
  ), [navigate, areThereUnreadNews, currentLanguage])

  const Content = useMemo(() => ({ close }: IHoverCardContent) => (
    <div className={styles.hoverCard}>
      {(notificationsData?.items || []).map(item => (
        <Link
          key={item.id}
          className={styles.item}
          to={`/notifications?tab=${tabs[item.category]}&id=${item.id}`}
          onClick={close}
        >
          {icons[item.category] ?? (<div className={styles.itemIcon} />)}
          <div className={styles.itemTitle} key={item.id}>
            <Title2 color="black" weight="semiBold">{item.title}</Title2>
            <Title3 color="grey-700" ellipsis lines={2}>
              {item.description}
            </Title3>
          </div>
        </Link>
      ))}
      <div className={styles.divider} />
      <div className={styles.footer}>
        <LinkButton
          to={'/notifications?tab=news'}
          title={
            <>
              {t("notificationWidget.all")}
              <Icon icon={ChevronRightIcon} />
            </>
          }
          size={LinkButtonSize.M}
        />
      </div>
    </div>
  ), [notificationsData?.items, currentLanguage])

  useEffect(() => {
    refetch();
  }, [currentLanguage])

  useEffect(() => {
    if (notificationsCount) {
      const { all } = notificationsCount;
      setAreThereUnreadNews(all);
    }
  }, [notificationsCount]);

  if (isLoading) {
    return (
      <Skeleton width="34px" height="34px" />
    )
  }

  return (
    <HoverCard
      Action={Action}
      Content={Content}
    />
  );
};

export default NotificationsWidget;
